import { Link } from "react-router-dom";
import usePageSEO from "@/hooks/usePageSEO";
import JsonLd from "@/components/feature/JsonLd";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://ghbeauty.kr";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl + "/" },
    { "@type": "ListItem", "position": 2, "name": "About", "item": siteUrl + "/about" }
  ]
};

export default function AboutPage() {
  usePageSEO({
    title: "About GH Corporation | Korean K-Beauty Export Partner",
  });

  return (
    <section className="w-full px-6 md:px-12 lg:px-20 pt-28 pb-24 md:pb-32 bg-background-50">
      <JsonLd data={breadcrumbSchema} />
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-accent-500" />
            <span className="font-label text-[11px] tracking-[0.22em] uppercase text-accent-700">
              About GH
            </span>
          </div>
          <h1 className="font-heading text-foreground-950 text-4xl md:text-5xl lg:text-6xl leading-[1.05] font-light">
            One trusted partner
            <br />
            <span className="italic">for every K-beauty</span>
            <br />
            export need.
          </h1>
          <div className="mt-10 space-y-4">
            <div className="aspect-[5/6] rounded-2xl overflow-hidden">
              <img
                src="https://storage.readdy-site.link/project_files/35b57ced-d75b-4532-9a59-9b08ce476502/765660c0-fbe2-4e63-b002-d24efd992999_compressed_1.webp"
                alt="GH cosmetics warehouse and export operations"
                title="GH cosmetics export warehouse"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="aspect-[4/3] rounded-2xl overflow-hidden">
              <img
                src="https://storage.readdy-site.link/project_files/35b57ced-d75b-4532-9a59-9b08ce476502/1a407a13-3f8c-4f18-bae4-76ab7a4d1f06_compressed_6.webp"
                alt="GH Corporation warehouse forklift loading Korean beauty products for export"
                title="GH Corporation warehouse loading operations"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 lg:pl-6">
          <p className="text-foreground-700 text-lg md:text-xl leading-relaxed font-light">
            Since 2012, GH Corporation has built practical experience in international trade and distribution. Based in Korea, we now specialize in supplying authentic Korean beauty products to overseas importers, distributors and retailers. Our strength lies in combining multi-brand sourcing, export coordination and flexible logistics through one dependable point of contact.
          </p>

          <p className="mt-6 text-foreground-700 text-lg md:text-xl leading-relaxed font-light">
            Built on international trade and distribution experience since 2012, GH Corporation combines Korean beauty sourcing, export preparation and global logistics support in one practical service structure.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <div className="p-5 rounded-2xl bg-background-100 border border-background-200/70 flex-1">
              <p className="font-heading text-foreground-950 text-xl italic">
                &ldquo;Your trusted Korea-side sourcing and export partner.&rdquo;
              </p>
            </div>
            <div className="p-5 rounded-2xl bg-background-100 border border-background-200/70 flex-1">
              <p className="font-heading text-foreground-950 text-xl italic">
                &ldquo;More than a supplier &mdash; a practical growth partner for global beauty buyers.&rdquo;
              </p>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-8">
            {[
              {
                icon: "ri-shield-check-line",
                title: "Verified Korean Supply Network",
                desc: "Access to authentic products from 125+ leading Korean beauty brands through verified domestic supply channels.",
              },
              {
                icon: "ri-ship-line",
                title: "Integrated Logistics Support",
                desc: "FCL, LCL and air-freight coordination with export packing, documentation and shipment management.",
              },
              {
                icon: "ri-global-line",
                title: "Proven Global Reach",
                desc: "Export experience across markets including North America, Latin America, Europe, the Middle East and Central Asia.",
              },
              {
                icon: "ri-customer-service-2-line",
                title: "Single Point of Contact",
                desc: "One English-speaking account manager handles sourcing, paperwork, shipping and after-sales — start to finish.",
              },
            ].map((b) => (
              <div
                key={b.title}
                className="group p-6 rounded-2xl border border-background-200/70 bg-background-50 hover:border-accent-300 transition-colors"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-700 group-hover:bg-accent-500 group-hover:text-background-50 transition-colors">
                  <i className={`${b.icon} text-xl`} />
                </div>
                <h3 className="mt-5 font-heading text-foreground-950 text-2xl">
                  {b.title}
                </h3>
                <p className="mt-3 text-foreground-700 text-[15px] leading-relaxed font-light">
                  {b.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 p-8 rounded-2xl bg-secondary-100 border border-secondary-200">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <div className="font-label text-[11px] tracking-[0.22em] uppercase text-secondary-700">
                  How we work
                </div>
                <p className="mt-2 font-heading text-secondary-900 text-2xl md:text-3xl">
                  RFQ &rarr; Quotation &rarr; Order &rarr; Shipping &rarr; Delivered
                </p>
              </div>
              <Link
                to="/contact"
                className="whitespace-nowrap cursor-pointer inline-flex items-center gap-3 px-5 py-3 rounded-full bg-secondary-900 text-background-50 hover:bg-secondary-700 transition-colors font-label text-[12px] tracking-[0.18em] uppercase"
              >
                Start an Inquiry
                <i className="ri-arrow-right-up-line" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}